import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  GeoJSONSource,
  Layer,
  type LngLat,
  type LngLatBoundsLike,
  MapLayerMouseEvent,
  Source,
  useMap,
} from "veneer/Map";

import {
  INDICATOR_COLORS,
  INDICATOR_TYPES,
  type IndicatorColor,
  type IndicatorType,
  getIndicatorImageId,
  getIndicatorSvgMarkup,
} from "cortex/ui/src/ui/indicator";

import type {
  DevicesFeatureCollection,
  SpiderLinesFeatureCollection,
} from "./action";
import {
  CLUSTER_EXPAND_ZOOM_THRESHOLD,
  CLUSTER_FIT_DURATION_MS,
  CLUSTER_FIT_MAX_ZOOM,
  CLUSTER_FIT_PADDING,
  CLUSTER_LEAVES_OFFSET,
  CLUSTER_LEAVES_PAGE_SIZE,
  CLUSTER_RADIUS_STEPS,
  CLUSTER_STROKE_WIDTH,
  COLORS,
  GET_MARKER_COLOR,
  GET_POINT_COUNT,
  MAX_CLUSTER_ZOOM,
} from "./constant";
import { DevicePopup, type DevicePopupState } from "../component/DevicePopup";

interface DevicesSourcesProps {
  devicesData: DevicesFeatureCollection;
  spiderLinesData: SpiderLinesFeatureCollection;
}

export function DevicesSources({
  devicesData,
  spiderLinesData,
}: DevicesSourcesProps) {
  return (
    <>
      <Source
        cluster
        clusterMaxZoom={MAX_CLUSTER_ZOOM}
        clusterProperties={{
          connected_count: [
            "+",
            ["case", ["==", GET_MARKER_COLOR, "green"], 1, 0],
          ],
          missed_checkin_count: [
            "+",
            ["case", ["==", GET_MARKER_COLOR, "yellow"], 1, 0],
          ],
          offline_count: [
            "+",
            ["case", ["==", GET_MARKER_COLOR, "darkYellow"], 1, 0],
          ],
          unreachable_count: [
            "+",
            ["case", ["==", GET_MARKER_COLOR, "red"], 1, 0],
          ],
          never_connected_count: [
            "+",
            ["case", ["==", GET_MARKER_COLOR, "gray"], 1, 0],
          ],
        }}
        clusterRadius={50}
        data={devicesData}
        id="devices"
        type="geojson"
      />
      <Source data={spiderLinesData} id="spider-lines" type="geojson" />
    </>
  );
}

interface DevicesLayersProps {
  maxZoom: number;
  onViewDeviceDetails?: (deviceId: string) => void;
}

function getIndicatorColor(markerColor?: string): IndicatorColor {
  if (markerColor === "darkYellow") {
    return "darkYellow";
  }

  if (markerColor === "gray") {
    return "gray";
  }

  if (markerColor === "green") {
    return "green";
  }

  if (markerColor === "red") {
    return "red";
  }

  return "yellow";
}

function isLngLat(value: unknown): value is LngLat {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    typeof value[0] === "number" &&
    typeof value[1] === "number"
  );
}

function boundsFromLngLats(coords: LngLat[]): LngLatBoundsLike | null {
  if (!coords.length) return null;

  let minLng = coords[0][0];
  let maxLng = coords[0][0];
  let minLat = coords[0][1];
  let maxLat = coords[0][1];

  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

export function DevicesLayers({
  maxZoom,
  onViewDeviceDetails,
}: DevicesLayersProps) {
  const { current: mapRef } = useMap();
  const map = mapRef.getMap();
  const [popupState, setPopupState] = useState<DevicePopupState | null>(null);

  useEffect(() => {
    if (!map) {
      return;
    }

    let isDisposed = false;

    INDICATOR_TYPES.forEach((type) => {
      INDICATOR_COLORS.forEach((color) => {
        const imageId = getIndicatorImageId(type, color);

        if (map.hasImage(imageId)) {
          return;
        }

        const image = new Image(20, 20);
        image.onload = () => {
          if (!isDisposed && !map.hasImage(imageId)) {
            map.addImage(imageId, image);
          }
        };
        image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
          getIndicatorSvgMarkup({
            color,
            size: 20,
            type,
          }),
        )}`;
      });
    });

    return () => {
      isDisposed = true;
    };
  }, [map]);

  const handleClusterClick = useCallback(
    (event: MapLayerMouseEvent) => {
      if (!map) return;

      const feature = event.features?.[0] as any;
      if (!feature?.properties) return;

      const clusterId = feature.properties.cluster_id as number | undefined;
      if (clusterId == null) return;

      const source = map.getSource("devices") as GeoJSONSource;

      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err || zoom == null) return;

        if (zoom > CLUSTER_EXPAND_ZOOM_THRESHOLD) {
          source.getClusterLeaves(
            clusterId,
            CLUSTER_LEAVES_PAGE_SIZE,
            CLUSTER_LEAVES_OFFSET,
            (leafErr, leaves) => {
              if (leafErr || !leaves?.length) return;

              const coords: LngLat[] = leaves
                .map((leaf: any) => (leaf?.geometry as any)?.coordinates)
                .filter(isLngLat);

              const bounds = boundsFromLngLats(coords);
              if (!bounds) return;

              map.fitBounds(bounds, {
                duration: CLUSTER_FIT_DURATION_MS,
                maxZoom: CLUSTER_FIT_MAX_ZOOM,
                padding: CLUSTER_FIT_PADDING,
              });
            },
          );
          return;
        }

        const coords = (feature.geometry as any)?.coordinates;
        if (!isLngLat(coords)) return;

        map.easeTo({
          center: coords,
          padding: CLUSTER_FIT_PADDING,
          zoom,
        });
      });
    },
    [map],
  );

  const handleUnclusteredPointClick = useCallback(
    (event: MapLayerMouseEvent) => {
      if (!map) return;

      const feature = event.features?.[0] as any;
      if (!feature?.properties) return;

      const coords = (feature.geometry as any)?.coordinates;
      if (!isLngLat(coords)) return;

      const props = feature.properties as {
        id: string;
        markerColor?: string;
        markerIndicator?: IndicatorType;
        markerLabel: string;
        name: string;
        networkName: string;
      };

      setPopupState({
        coords,
        id: props.id,
        indicatorColor: getIndicatorColor(props.markerColor),
        markerIndicator: props.markerIndicator,
        markerLabel: props.markerLabel,
        name: props.name,
        networkName: props.networkName,
      });

      map.easeTo({
        center: coords,
        duration: CLUSTER_FIT_DURATION_MS / 2,
        offset: [CLUSTER_LEAVES_OFFSET, CLUSTER_LEAVES_PAGE_SIZE],
        zoom: Math.min(map.getZoom(), maxZoom),
      });
    },
    [map, maxZoom],
  );

  const setCursor = useCallback(
    (cursor: string) => {
      if (!map) return;
      map.getCanvas().style.cursor = cursor;
    },
    [map],
  );

  const handlePointEnter = useCallback(() => {
    setCursor("pointer");
  }, [setCursor]);

  const handlePointLeave = useCallback(() => {
    setCursor("");
  }, [setCursor]);

  const popupContent = useMemo(() => {
    return popupState ? (
      <DevicePopup
        onClose={() => setPopupState(null)}
        onViewDetails={onViewDeviceDetails}
        popupState={popupState}
      />
    ) : null;
  }, [onViewDeviceDetails, popupState]);

  return (
    <>
      <Layer
        filter={["has", "point_count"]}
        id="clusters"
        onClick={handleClusterClick}
        onMouseEnter={handlePointEnter}
        onMouseLeave={handlePointLeave}
        paint={{
          "circle-color": COLORS.white,
          "circle-radius": CLUSTER_RADIUS_STEPS,
          "circle-stroke-color": [
            "case",
            ["==", ["get", "connected_count"], GET_POINT_COUNT],
            COLORS.green,
            ["==", ["get", "never_connected_count"], GET_POINT_COUNT],
            COLORS.gray,
            ["==", ["get", "unreachable_count"], GET_POINT_COUNT],
            COLORS.red,
            ["==", ["get", "missed_checkin_count"], GET_POINT_COUNT],
            COLORS.yellow,
            ["==", ["get", "offline_count"], GET_POINT_COUNT],
            COLORS.darkYellow,
            COLORS.orange,
          ],
          "circle-stroke-width": CLUSTER_STROKE_WIDTH,
        }}
        source="devices"
        type="circle"
      />
      <Layer
        filter={["has", "point_count"]}
        id="cluster-count"
        layout={{
          "text-anchor": "center",
          "text-field": [
            "format",
            GET_POINT_COUNT,
            {
              "font-scale": ["step", GET_POINT_COUNT, 0.9, 5, 1, 10, 1.2],
            },
          ],
        }}
        source="devices"
        type="symbol"
      />
      <Layer
        filter={["!", ["has", "point_count"]]}
        id="unclustered-point"
        layout={{
          "icon-allow-overlap": true,
          "icon-image": [
            "concat",
            "map-indicator-",
            ["coalesce", ["get", "markerIndicator"], "circle"],
            "-",
            ["coalesce", ["get", "markerColor"], "gray"],
          ],
        }}
        onClick={handleUnclusteredPointClick}
        onMouseEnter={handlePointEnter}
        onMouseLeave={handlePointLeave}
        source="devices"
        type="symbol"
      />
      <Layer
        id="spider-lines"
        paint={{
          "line-color": COLORS.gray,
          "line-dasharray": [2, 2],
          "line-width": 1,
        }}
        source="spider-lines"
        type="line"
      />
      {popupContent}
    </>
  );
}
